import React, { useState } from "react";
import api from "@/api";
import TrialsTable from "../TrialsTable";
import { MAX_EXPORT_TRIALS } from "@/utils/excelReport/exportExcelReport";
import { MAX_SHOWN_TRIALS } from "./ShowReport";

// The selection is capped at what the Excel export takes (it fetches slim data a page at a time); the report on
// screen shows the first MAX_SHOWN_TRIALS of it (ShowReport).
const MAX_SELECTED_TRIALS = MAX_EXPORT_TRIALS;

const formatCount = (n) => n.toLocaleString("en-US");

/**
 * Step "Select Trials": the trials table with the full search panel and checkboxes.
 * The selection is the parent's (ExistingLayoutFlow) list of ids, so it survives searching,
 * paging and changing filters. Export to Excel is on Show Report: the file has the layout's sheet.
 */
export default function SelectReportData({ onSelectTrials, selectedTrialIDs = [], selectedLayoutId = null }) {
  const [results, setResults] = useState(null); // { total, query } of the current search
  const [selectingAll, setSelectingAll] = useState(false);
  const [notice, setNotice] = useState(null);

  // Every selection change goes through here, so the limit also applies to selecting page by page
  const updateSelection = (ids) => {
    const unique = [...new Set(ids)];
    if (unique.length > MAX_SELECTED_TRIALS) {
      setNotice(`At most ${formatCount(MAX_SELECTED_TRIALS)} trials can be selected, so only the first ${formatCount(MAX_SELECTED_TRIALS)} were selected.`);
      onSelectTrials?.(unique.slice(0, MAX_SELECTED_TRIALS));
      return;
    }
    setNotice(null);
    onSelectTrials?.(unique);
  };

  const selectAllResults = async () => {
    if (!results) return;
    setSelectingAll(true);
    try {
      const res = await api.post("/trials", { ...results.query, ids_only: true });
      updateSelection([...selectedTrialIDs, ...(res.data?.ids ?? [])]);
    } catch {
      setNotice("Could not select all results. Please try again.");
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => {
    setNotice(null);
    onSelectTrials?.([]);
  };

  const selectedCount = selectedTrialIDs.length;

  return (
    <div className="w-full p-4">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Select Trials</h1>
      </div>

      <div
        className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
        aria-live="polite"
      >
        <span className="font-semibold" data-testid="selected-count">
          {formatCount(selectedCount)} {selectedCount === 1 ? "trial" : "trials"} selected
        </span>
        {results && results.total > 0 && (
          <button
            type="button"
            onClick={selectAllResults}
            disabled={selectingAll}
            className="font-medium text-emerald-700 underline-offset-2 hover:underline disabled:opacity-50 dark:text-emerald-300"
          >
            {selectingAll ? "Selecting..." : `Select all ${formatCount(results.total)} results`}
          </button>
        )}
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={clearSelection}
            className="font-medium text-gray-600 underline-offset-2 hover:underline dark:text-gray-300"
          >
            Clear selection
          </button>
        )}
        <span className="text-xs text-emerald-800/80 dark:text-emerald-200/70">
          Max {formatCount(MAX_SELECTED_TRIALS)} trials; the report on screen shows the first {formatCount(MAX_SHOWN_TRIALS)}.
        </span>
        <span className="ml-auto text-sm text-emerald-800 dark:text-emerald-200" data-testid="excel-export-hint">
          {selectedLayoutId ? "Export to Excel is on Show Report" : "Choose a layout to export"}
        </span>
      </div>

      {notice && (
        <div role="alert" className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {notice}
        </div>
      )}

      <TrialsTable
        showSearchFilters={true}
        massUpdateMode={true}
        rowClickSelects={true}
        selectedTrialIds={selectedTrialIDs}
        onSelectionChange={updateSelection}
        onResultsChange={setResults}
        showActions={false}
        columnStorageKey="selectReportDataColumns"
        perPageStorageKey="selectReportDataPerPage"
      />
    </div>
  );
}
